import groupBy from "lodash/groupBy";

import {
  maxPriorityOption,
  PriorityOption,
  priorityOptions,
} from "@/features/purchasing-priority/purchasing-priority-utils";
import { BomHierarchyGenerator, BomNode } from "./bom-hierarchy-generator";
import { BomRecordsForParents } from "./bom-records";
import { CostTaxBreakdown } from "./cost-tax-breakdown";
import { ExistingInventoryAllocator, ExistingInventoryForItem } from "./existing-inventory";
import { Item } from "./items";
import { PurchasingPriorities } from "./purchasing-priorities";

export interface BomComponent extends QuantitiesForPriority {
  item: Item;
  isAssembly: boolean;
  onSalesOrder: boolean;

  /**
   * A map of purchasing priority to the quantities required for that priority.
   *
   * The quantities for lower priorities also includes the quantities for higher priorities. As such, to get the total
   * quantities while ignoring priority, use the highest priority number in the map, which encapsulates all priorities.   *
   */
  quantitiesByPriority: Map<PriorityOption, QuantitiesForPriority>;

  /**
   * For non-assemblies, the purchase cost of a single unit from the preferred vendor, as determined by the 'items'
   * import stage.
   *
   * For assemblies, the sum total of the `unitCost` value on it's children.
   */
  unitCost: CostTaxBreakdown;
}

/**
 * The quantities for a BOM component at a given priority level.
 */
export interface QuantitiesForPriority {
  /** The total quantity of this item found on the BOM at the relevant priority level. */
  bomTotalQuantity: number;

  /**
   * The number of this item that is taken from (positive), or returned to (negative) existing inventory.
   *
   * NOTE: This may not match the amount from `existingInventory` stage, since only the amount used in the BOM is included.
   *       For example, if 10 of an item are moved to the sales order rack from inventory, but only 5 are used in the BOM,
   *       this value will be 5, but the existing inventory tab will show 10.
   */
  existingInventoryQuantity: number;

  /**
   * This is the quantity of this item that is required to purchase/build (i.e. not pull from existing inventory) at the
   * relevant priority level.
   *
   * This value is calculated recursively, that is, if a parent assembly is taken from existing inventory, the required
   * child components also have their required quantity reduced by the appropriate amount.
   */
  nonInventoryQuantity: number;

  /**
   * The quantity required for the current job at a given priority level is the sum of the existing inventory quantity
   * at that priority level and the non-inventory quantity.
   *
   * NOTE: This is NOT always the same as the total quantity on the BOM. If any parent assemblies are taken
   * from existing inventory, the total quantity of it's components will be less than the total quantity in the complete
   * BOM.
   */
  quantityRequiredForJob: number;
}

const zeroQuantities: QuantitiesForPriority = {
  bomTotalQuantity: 0,
  existingInventoryQuantity: 0,
  nonInventoryQuantity: 0,
  quantityRequiredForJob: 0,
};

const zeroQuantitiesByPriority = new Map<PriorityOption, QuantitiesForPriority>(
  priorityOptions.map((priority) => [priority, zeroQuantities]),
);

/** Flatten the sales order BOM hierarchy into a list of components. */
export function getSalesOrderBomComponents(
  salesOrderBomHierarchy: BomNode[],
  existingInventory: ExistingInventoryForItem[],
): BomComponent[] {
  // Simulate the parent quantities as 1 unit, not pulled from existing inventory, since this is the top-level of the BOM
  const parentQuantities: QuantitiesForPriority = {
    bomTotalQuantity: 1,
    existingInventoryQuantity: 0,
    nonInventoryQuantity: 1,
    quantityRequiredForJob: 1,
  };

  // Create a map of existing inventory allocators, one for each priority option
  const existingInventoryAllocators = new Map<PriorityOption, ExistingInventoryAllocator>(
    priorityOptions.map((priority) => [priority, new ExistingInventoryAllocator(existingInventory)]),
  );

  return groupBomByComponent(salesOrderBomHierarchy, existingInventoryAllocators, parentQuantities, true);
}

/** Combine the sales order BOM components with the items not found on the sales order */
export function getBomByComponent(
  salesOrderComponents: BomComponent[],
  itemsNotOnSalesOrderBomRecords: BomRecordsForParents,
  purchasingPriorities: PurchasingPriorities,
): BomComponent[] {
  // Create a map of dummy existing inventory allocators that never allocate anything. We don't need to allocate existing
  // inventory to items not on the sales order
  const dummyAllocator = new ExistingInventoryAllocator([]);
  const dummyAllocators = new Map<PriorityOption, ExistingInventoryAllocator>(
    priorityOptions.map((priority) => [priority, dummyAllocator]),
  );

  // Create a second list of components based on items not found on the sales order; these are items found on various
  // records assigned to the job, but are not part of the BOM derived from the sales order line items
  // NOTE: Only the top-level nodes are included in this part of the BOM; children are only used to calculate costs
  // NOTE: This step is performed because the total quantity for all these components is zero, but we don't want to
  // filter them out; they are useful to include because they represent the 'actual' BOM
  const supplementaryHierarchy = new BomHierarchyGenerator(purchasingPriorities)
    .generate(itemsNotOnSalesOrderBomRecords)
    .map<BomNode>((node) => ({ ...node, children: [] }));
  const supplementaryComponents = groupBomByComponent(supplementaryHierarchy, dummyAllocators, zeroQuantities, false);

  // Combine both lists of components, items not on the sales order are sorted to the end of the list
  return [...salesOrderComponents, ...supplementaryComponents];
}

function groupBomByComponent(
  bomHierarchy: BomNode[],
  existingInventoryAllocators: Map<PriorityOption, ExistingInventoryAllocator>,
  parentMaxQuantities: QuantitiesForPriority,
  onSalesOrder: boolean,
): BomComponent[] {
  // Flatten the BOM hierarchy into a list of BOM components, one component per node
  // This list will contain duplicates of items that appear in multiple places in the BOM hierarchy
  const flattenedNodes = bomHierarchy.flatMap((node) => {
    const { item, isAssembly, unitCost, children } = node;

    // Calculate the quantities for this node at each priority level
    const quantitiesByPriority = new Map<PriorityOption, QuantitiesForPriority>(
      priorityOptions.map((priority) => [
        priority,
        calculateQuantitiesForPriority(node, priority, parentMaxQuantities, existingInventoryAllocators.get(priority)!),
      ]),
    );

    const parentRecord: BomComponent = {
      item,
      isAssembly,
      unitCost,
      onSalesOrder,
      quantitiesByPriority,
      ...zeroQuantities, // This is a placeholder to satisfy TypeScript, the real values are set when nodes are combined below
    };
    const childRecords = groupBomByComponent(
      children,
      existingInventoryAllocators,
      quantitiesByPriority.get(maxPriorityOption)!,
      onSalesOrder,
    );
    return [parentRecord, ...childRecords];
  });

  // Group the flattened nodes by item ID, then reduce the groups into a single entry for each item
  // Sort the entries before returning by item type (descending) and item name (ascending)
  return Object.values(groupBy(flattenedNodes, (x) => x.item.id))
    .map<BomComponent>((groupNodes) => {
      const quantitiesByPriority = groupNodes.reduce(
        (acc, x) => addQuantitiesByPriority(acc, x.quantitiesByPriority),
        zeroQuantitiesByPriority,
      );
      return {
        ...groupNodes[0]!,
        quantitiesByPriority,
        ...quantitiesByPriority.get(maxPriorityOption)!,
      };
    })
    .sort((a, b) => b.item.type.localeCompare(a.item.type) || a.item.name.localeCompare(b.item.name));
}

function calculateQuantitiesForPriority(
  node: BomNode,
  priority: PriorityOption,
  parentMaxQuantities: QuantitiesForPriority,
  existingInventoryAllocator: ExistingInventoryAllocator,
): QuantitiesForPriority {
  // Return a zero result if the node's priority is higher (numerically) than the current priority
  if (node.purchasingPriority.priority > priority) return zeroQuantities;

  // Calculate the total quantity as found on the BOM
  const bomLineQuantity = node.quantity;
  const bomTotalQuantity = parentMaxQuantities.bomTotalQuantity * bomLineQuantity;

  // Calculate the total quantity required for the job by multiplying the current node's quantity by the parent's non-inventory quantity
  // By using the parent's non-inventory quantity instead of the total quantity, we ensure that if a parent assembly
  // is pulled from existing inventory, the child components will also have their required quantity reduced accordingly
  const quantityRequiredForJob = parentMaxQuantities.nonInventoryQuantity * bomLineQuantity;

  // Calculate the amount to use from existing inventory; Make sure to subtract any allocated inventory from the existing inventory map
  const existingInventoryQuantity = existingInventoryAllocator.tryAllocate(node.item.id, quantityRequiredForJob);

  // The non-inventory quantity is the total quantity minus the existing inventory quantity
  const nonInventoryQuantity = quantityRequiredForJob - existingInventoryQuantity;

  return {
    bomTotalQuantity,
    quantityRequiredForJob,
    existingInventoryQuantity,
    nonInventoryQuantity,
  };
}

function addQuantitiesByPriority(
  a: Map<PriorityOption, QuantitiesForPriority>,
  b: Map<PriorityOption, QuantitiesForPriority>,
): Map<PriorityOption, QuantitiesForPriority> {
  return new Map(
    priorityOptions.map((priority) => [priority, addQuantitiesForPriority(a.get(priority)!, b.get(priority)!)]),
  );
}

function addQuantitiesForPriority(a: QuantitiesForPriority, b: QuantitiesForPriority): QuantitiesForPriority {
  return {
    bomTotalQuantity: a.bomTotalQuantity + b.bomTotalQuantity,
    existingInventoryQuantity: a.existingInventoryQuantity + b.existingInventoryQuantity,
    nonInventoryQuantity: a.nonInventoryQuantity + b.nonInventoryQuantity,
    quantityRequiredForJob: a.quantityRequiredForJob + b.quantityRequiredForJob,
  };
}
